// Lightweight, dependency-free JWT helpers used to decide — on the client —
// whether the stored access token is still usable. This lets route guards and
// the axios layer treat an expired token as "logged out" instead of waiting for
// the backend to answer 401 (which previously let users wander the app with
// empty data and no redirect).

/**
 * Decode a JWT payload without verifying the signature (client-side only).
 * Returns null for anything that is not a well-formed JWT.
 */
export const decodeToken = (token) => {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  try {
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
    const json = decodeURIComponent(
      atob(padded)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
};

/**
 * True when the token is missing, malformed, has no `exp`, or has already
 * expired. A small clock-skew margin avoids races right at the boundary.
 * Fail-closed: if we cannot read an expiry, treat it as expired.
 *
 * @param {string} token
 * @param {number} skewSeconds tolerance (default 30s)
 */
export const isTokenExpired = (token, skewSeconds = 30) => {
  const payload = decodeToken(token);
  if (!payload || typeof payload.exp !== 'number') return true;
  const nowSeconds = Date.now() / 1000;
  return payload.exp <= nowSeconds + skewSeconds;
};

/**
 * Milliseconds until the token expires, or 0 if already expired/unknown.
 * Used to schedule a proactive auto-logout while the user is idle on a page.
 */
export const millisUntilExpiry = (token) => {
  const payload = decodeToken(token);
  if (!payload || typeof payload.exp !== 'number') return 0;
  return Math.max(0, payload.exp * 1000 - Date.now());
};
